import { FlagEvaluationContext, FlagRule } from './types';

/** Parse an ISO string or epoch-millis value to epoch millis, or NaN if invalid. */
const toEpochMillis = (value: string | number | undefined): number => {
  if (value === undefined || value === null) return NaN;
  if (typeof value === 'number') return value;
  return Date.parse(value);
};

const nonEmpty = (arr?: string[]): string[] => (Array.isArray(arr) ? arr.filter(Boolean) : []);

/**
 * Does the user (alias / groups) satisfy the rule's audience?
 * Empty/absent audience means no one is targeted via this dimension → returns false.
 * (The caller ORs this with resourceMatches, so a non-empty resource can still pass.)
 */
const audienceMatches = (rule: FlagRule, ctx: FlagEvaluationContext): boolean => {
  const aliases = nonEmpty(rule.audience?.aliases);
  const groups = nonEmpty(rule.audience?.posixGroups);
  if (aliases.length === 0 && groups.length === 0) return false; // nothing configured → no match

  if (aliases.includes(ctx.userAlias)) return true;
  const userGroups = nonEmpty(ctx.userGroups);
  return groups.some(g => userGroups.includes(g));
};

/**
 * Does the current resource (policy / folder) satisfy the rule's scope?
 * Empty/absent scope means no resource is targeted via this dimension → returns false.
 * (The caller ORs this with audienceMatches, so a non-empty audience can still pass.)
 * When both policy and folder targets exist, either match is sufficient (OR).
 */
const resourceMatches = (rule: FlagRule, ctx: FlagEvaluationContext): boolean => {
  const policyIds = nonEmpty(rule.resourceScope?.policyIds);
  const folderIds = nonEmpty(rule.resourceScope?.folderIds);
  if (policyIds.length === 0 && folderIds.length === 0) return false; // nothing configured → no match

  if (ctx.policyId && policyIds.includes(ctx.policyId)) return true;

  if (folderIds.length > 0) {
    if (ctx.folderId && folderIds.includes(ctx.folderId)) return true;
    const includeDescendants = rule.resourceScope?.includeDescendants !== false; // default true
    if (includeDescendants) {
      const ancestors = nonEmpty(ctx.folderAncestorIds);
      if (ancestors.some(id => folderIds.includes(id))) return true;
    }
  }
  return false;
};

/**
 * Does the exception's raised time satisfy the rule's time window?
 * No window configured → always matches. If a window is set but the context
 * carries no raised time, we cannot confirm eligibility → conservative false.
 * Comparison is done in UTC epoch millis to avoid timezone drift.
 */
const raisedWindowMatches = (rule: FlagRule, ctx: FlagEvaluationContext): boolean => {
  const cutoffRaw = rule.raisedWindow?.onlyIfRaisedAfter;
  if (!cutoffRaw) return true;

  const cutoff = toEpochMillis(cutoffRaw);
  const raisedAt = toEpochMillis(ctx.exceptionRaisedAt);
  if (Number.isNaN(cutoff)) return true; // misconfigured cutoff → don't block on it
  if (Number.isNaN(raisedAt)) return false; // window required but no/invalid timestamp
  return raisedAt >= cutoff;
};

export interface EvaluateOptions {
  /**
   * When true, evaluate only `enabled` + audience (alias / posix groups) and
   * skip resource scope + raised-time window. Use for early gates where the
   * resource context isn't available yet (e.g. before a document loads). The
   * full check should still run later where the context is known.
   */
  identityOnly?: boolean;
}

/**
 * Pure evaluation of a single flag rule against the runtime context.
 *
 * Evaluation uses OR across the two targeting dimensions (audience and
 * resource scope):
 *   - audience match  → alias in aliases OR user in posixGroups
 *   - resource match  → policyId in policyIds OR folderId/ancestor in folderIds
 *
 * A dimension with NO configuration (empty/absent lists) does NOT auto-pass —
 * it simply contributes nothing to the OR. This means:
 *   - Both dimensions empty → nothing explicitly allows the user → fail closed.
 *   - Only audience configured → alias/group match is sufficient, no folder needed.
 *   - Only resource configured → folder/policy match is sufficient, no alias needed.
 *   - Both configured → either one is enough (OR).
 *
 * raisedWindow is ANDed on top — it is a hard time gate (exception flows
 * only) that always passes when absent, so it never blocks the OR when unused.
 *
 * With `options.identityOnly`, only `enabled` + audience are checked (used
 * for early gates before the resource context is available).
 */
export const evaluateFlag = (
  rule: FlagRule | undefined | null,
  ctx: FlagEvaluationContext,
  options: EvaluateOptions = {},
): boolean => {
  if (!rule || !rule.enabled) return false;
  if (options.identityOnly) return audienceMatches(rule, ctx);
  // Audience OR resource — either targeting dimension is sufficient.
  // Empty dimensions do not auto-pass; they simply don't contribute to the OR.
  const targetingPasses = audienceMatches(rule, ctx) || resourceMatches(rule, ctx);
  // raisedWindow is a hard AND gate (only meaningful for exception flows;
  // returns true when no window is configured so it never blocks otherwise).
  return targetingPasses && raisedWindowMatches(rule, ctx);
};

/** Convenience: find a rule by key in a list and evaluate it. */
export const isAgentEnabled = (
  rules: FlagRule[] | undefined | null,
  flagKey: string,
  ctx: FlagEvaluationContext,
  options?: EvaluateOptions,
): boolean => {
  const rule = (rules || []).find(r => r.flagKey === flagKey);
  return evaluateFlag(rule, ctx, options);
};
